// MIDAS V3 — Empty thread welcome state (Phase 2)
//
// Per spec §12: when `messages.length === 0` for the active thread, render
// the welcome variant — Sora 30 title + machine context subline. Copy comes
// from v2 `ChatWelcomeV2` but restyled with v3 tokens.
//
// Visual: centred column, max-width 520, dim mono eyebrow + bold title +
// muted subtitle. Includes MidasLogoV3 for brand presence (a single-line
// floating "M" tile, no halo — halo arrives in Phase 6 with streaming).
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React from "react";

import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import MidasLogoV3 from "./MidasLogoV3";

const ChatWelcomeV3: React.FC = () => {
  const { translate } = useTranslation("chatBot");
  const machineContext = useAppSelector((s) => s.chatBot.machineContext);

  const model = machineContext?.model?.trim() || "";
  const brand = machineContext?.manufacturerName?.trim() || "";
  const machineName = model ? (brand ? `${brand} ${model}` : model) : "";

  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 24px",
      }}
    >
      <div
        style={{
          maxWidth: 520,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 16,
          textAlign: "center",
        }}
      >
        <MidasLogoV3 size={48} />
        <span
          className="mono"
          style={{
            fontSize: 10,
            letterSpacing: "0.12em",
            color: "var(--faint)",
            textTransform: "uppercase",
          }}
        >
          {translate("midas.v3.welcome.eyebrow", {
            defaultValue: "MIDAS · ASSISTANT",
          }) as string}
        </span>
        <h2
          style={{
            margin: 0,
            fontFamily: "var(--font-sans)",
            fontSize: 30,
            fontWeight: 700,
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
            color: "var(--ink)",
          }}
        >
          {translate("midas.v3.welcome.title", {
            defaultValue: "Start a conversation with Midas",
          }) as string}
        </h2>
        <p
          style={{
            margin: 0,
            fontSize: 14,
            lineHeight: 1.5,
            color: "var(--muted)",
            fontFamily: "var(--font-sans)",
            letterSpacing: "-0.005em",
            maxWidth: 440,
          }}
        >
          {translate("midas.v3.welcome.subtitle", {
            defaultValue:
              "Ask about hydraulic schematics, alarm codes, or service history — Midas will pull the right citations as it goes.",
          }) as string}
        </p>
        {machineName && (
          <span
            className="mono"
            style={{
              marginTop: 4,
              padding: "4px 12px",
              borderRadius: 999,
              border: "1px solid var(--border)",
              background: "var(--card)",
              fontSize: 11,
              color: "var(--ink-2)",
              letterSpacing: "0.04em",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: "inline-block",
                width: 6,
                height: 6,
                borderRadius: 999,
                background: "var(--green)",
                marginRight: 8,
                verticalAlign: "middle",
              }}
            />
            {machineName}
          </span>
        )}
      </div>
    </div>
  );
};

export default ChatWelcomeV3;
